import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import type { PhenotypeResult } from "@/lib/analysis";

/**
 * Derived resistance markers (AN-09 … AN-11).
 *
 * A marker flagged `is_proxy` is a screening inference, not a confirmed result,
 * and it is labelled as such here. Presenting an ESBL screen as a confirmed
 * ESBL rate would mislead a prescriber, which is the one thing a surveillance
 * report must not do.
 */
export function PhenotypePanel({ phenotypes }: { phenotypes: PhenotypeResult[] }) {
  if (phenotypes.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title="Resistance markers"
        description="Derived from the results already entered. No extra testing needed."
      />
      <CardBody className="grid gap-3 sm:grid-cols-2">
        {phenotypes.map((item) => (
          <div
            key={item.code}
            className="rounded-md border border-slate-200 p-3"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-slate-900">{item.code}</p>
                <p className="text-xs text-slate-600">{item.label}</p>
              </div>
              {item.is_proxy && <Badge tone="warning">screen only</Badge>}
            </div>

            <p className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">
              {item.suppressed || item.percent === null ? (
                <span className="text-base font-normal text-slate-500">
                  {item.n_eligible === 0
                    ? "No eligible isolates"
                    : `${item.n_eligible} isolates — too few to report a percentage`}
                </span>
              ) : (
                <>
                  {item.percent}%
                  <span className="ml-2 text-sm font-normal text-slate-500">
                    {item.n_positive} of {item.n_eligible}
                  </span>
                </>
              )}
            </p>

            {item.note && (
              <p className="mt-2 text-xs text-slate-500">{item.note}</p>
            )}
          </div>
        ))}
      </CardBody>
    </Card>
  );
}
